// File: frontend/src/components/entries/EntryRow.tsx
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { RiMoreLine, RiEditLine, RiDeleteBinLine, RiPushpinLine, RiMailLine, RiAlarmWarningLine, RiDeleteBin6Line, RiHashtag } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import "./EntryRow.css";
import { EntryModel } from "../../models";
import { updateEntry, deleteEntry } from "../../services/entryService";
import { parseApiError } from "../../utils/parseApiError";
import { hardDeleteFromTrash } from "../../services/trashService";
import { pinEntry, unpinEntry } from "../../services/profileService";
// Sitewide pins: admin/mod can pin this entry to the top of its topic page
import { adminPinService } from "../../services/pinService";
import { usePins } from "../../hooks/usePins";
import { PinTargetType } from "../../models/PinModel";
import { useAuth } from "../../context/AuthContext";
import { VoteTypeEnum } from "../../models/enums/VoteTypeEnum";
import { SportTypeEnum } from "../../models/enums/SportTypeEnum";
import { formatDateTime, generateProfileUrl } from "../../utils";
import { lowercaseEntryContent } from "../../utils/entryContentUtils";
import { usePreferences } from "../../context/PreferencesContext";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { castVote, getVoters, VoterResponse } from "../../services/voteService";
import { RateLimitError } from "../../services/apiClient";
import { useTranslation } from "react-i18next";
import { EntryEditorToolbar } from "./EntryEditorToolbar";
import { SpoilerBlock } from "./SpoilerBlock";
import { EntryImage, collectEntryImageUrls, normalizeImageTokenSpacing } from "./EntryImage";
import { renderBasicMarkup } from "../../utils/inlineMarkup";
import { findBlockToken } from "../../utils/markupUtils";
import { ConfirmModal } from "../common/ConfirmModal";
import { ReportModal } from "../common/ReportModal";
import { ShareMenu } from "../common/ShareMenu";
import { buildEntryShareUrl } from "../../utils/shareUtils";
import { entryPermalinkPath } from "../../utils/canonicalUrl";
import { getReportStatus } from "../../services/entryReportService";
import { deleteAdminEntry, updateAdminEntry } from "../../services/adminEntryService";
import { 
  FaFutbol, 
  FaBasketballBall, 
  FaVolleyballBall, 
  FaFootballBall, 
  FaBaseballBall, 
  FaHockeyPuck,
  FaHandPaper
} from "react-icons/fa";
import { TOAST_DURATION_SHORT, TOAST_DURATION_NORMAL } from "../../constants";
import { parseServerDate } from "../../utils/dateUtils";

import { ChipEditor, ChipEditorHandle } from "./ChipEditor";
// Get sport-specific favorite icon based on sportType
const getSportIcon = (sportType: SportTypeEnum, size: number = 14) => {
  switch (sportType) {
    case SportTypeEnum.FOOTBALL:
      return <FaFutbol size={size} />;
    case SportTypeEnum.BASKETBALL:
      return <FaBasketballBall size={size} />;
    case SportTypeEnum.VOLLEYBALL:
      return <FaVolleyballBall size={size} />;
    case SportTypeEnum.AMERICAN_FOOTBALL:
      return <FaFootballBall size={size} />;
    case SportTypeEnum.BASEBALL:
      return <FaBaseballBall size={size} />;
    case SportTypeEnum.ICE_HOCKEY:
      return <FaHockeyPuck size={size} />;
    case SportTypeEnum.HANDBALL:
      return <FaHandPaper size={size} />;
    default:
      return <FaFutbol size={size} />;
  }
};

// New format: (ref: label||matchId) — label has Turkish chars, matchId for navigation
// Bold: (b: text), Italic: (i: text), Link: (link: url|display text)
// Current match ref format: (mac: label||matchId) — inserted by RefSearchPopup
const MAC_REF_PATTERN = /\(mac:\s*(.*?)\|\|(\d+)\)/g;
// Legacy match ref format: (ref: label||matchId) — kept for backward compatibility with old entries
const REF_NEW_PATTERN = /\(ref:\s*(.*?)\|\|(\d+)\)/g;
const REF_LEGACY_PATTERN = /\((ref|bkz):\s*([^\s)]+)\)/g;
const BOLD_PATTERN = /\(b:\s*(.*?)\)/g;
const ITALIC_PATTERN = /\(i:\s*(.*?)\)/g;
const LINK_PATTERN = /\(link:\s*([^|)]+)\|([^)]+)\)/g;
const ENTRY_REF_PATTERN = /\(#(\d+)\)/g;
// Person reference: (kisi: displayName||slug) — inserted by the person ref toolbar button
const PERSON_REF_PATTERN = /\(kisi:\s*(.*?)\|\|([^)]+)\)/g;
// Club reference: (kulup: displayName||slug) — inserted by the club ref toolbar button
const CLUB_REF_PATTERN = /\(kulup:\s*(.*?)\|\|([^)]+)\)/g;
// Team reference: (takim: label||id) — inserted by the team ref toolbar button.
// Tails written before the id-only form carry "clubSlug/teamSlug" instead and
// still resolve, through the legacy two-segment route.
const TEAM_REF_PATTERN = /\(takim:\s*(.*?)\|\|([^)\s]+)\)/g;
// Custom topic reference: (baslik: title||slug) — inserted by the topic ref toolbar button
const TOPIC_REF_PATTERN = /\(baslik:\s*(.*?)\|\|([^)]+)\)/g;
// Parenthesized "(bkz: ...)" reference variants — inserted when the ref popup's
// bkz insert mode is selected. Same "||tail" semantics as their inline counterparts;
// rendered as a literal "(bkz: <link>)" note instead of an in-sentence link.
const BKZ_MAC_REF_PATTERN = /\(bkz-mac:\s*(.*?)\|\|(\d+)\)/g;
const BKZ_PERSON_REF_PATTERN = /\(bkz-kisi:\s*(.*?)\|\|([^)]+)\)/g;
const BKZ_CLUB_REF_PATTERN = /\(bkz-kulup:\s*(.*?)\|\|([^)]+)\)/g;
const BKZ_TEAM_REF_PATTERN = /\(bkz-takim:\s*(.*?)\|\|([^)\s]+)\)/g;
const BKZ_TOPIC_REF_PATTERN = /\(bkz-baslik:\s*(.*?)\|\|([^)]+)\)/g;
// Spoiler block: (spoiler: text) — collapsed by default, click to reveal.
// Quote block: (alinti: text) — pasted via the toolbar's alıntı modal.
// Both are located with findBlockToken() (markupUtils.tsx) instead of a
// single regex, since a regex can't reliably find the matching closing
// parenthesis once the pasted content contains its own "(" / ")" characters
// — see that function's comment for the full explanation. Only the token
// itself is matched: the whitespace around it belongs to the author and is
// rendered as typed.
// Inline image: (gorsel: <ref>) — inserted by EntryEditorToolbar görsel button.
// <ref> is either an opaque image id (new format, e.g. 8f14...543.jpg) or a
// full URL (legacy entries written before the opaque id format). Both keep
// working; resolution to a real src happens inside EntryImage.
const GORSEL_PATTERN = /\(gorsel:\s*(https?:\/\/[^)\s]+|[a-f0-9]{32}\.(?:jpg|jpeg|png|webp))\s*\)/g;

/**
 * Renders a (#id) reference as a clickable badge.
 * If the entry belongs to the currently displayed match it smooth-scrolls to it.
 * Otherwise it navigates to the entry's permalink (/entry/{id}), which
 * resolves the topic, the page number and the anchor server-side.
 */
const EntryIdRefLink: React.FC<{ entryId: string }> = ({ entryId }) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // Try scrolling on the current page first (same match)
    const el = document.getElementById(`entry-${entryId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    // Entry not on the current page — hand the id to the permalink route.
    //
    // This used to fetch the entry here and build a match URL from it, which
    // only ever worked for match entries and always landed on page 1: the
    // anchor is useless if the entry sits on page 4. /entry/{id} resolves the
    // topic AND the page number for whichever reader clicked, across all five
    // domains, so no fetch is needed here at all.
    navigate(entryPermalinkPath(Number(entryId)));
  };

  return (
    <a
      href={`#entry-${entryId}`}
      className="entry-id-ref"
      title={`Entry #${entryId}'e git`}
      onClick={handleClick}
    >
      #{entryId}
    </a>
  );
};

/**
 * Collect all formatted spans from the content string.
 * Returns them sorted by position so we can render left-to-right.
 */
interface ContentSpan {
  index: number;
  length: number;
  node: React.ReactNode;
}

/**
 * Normalize raw entry content before loading it into the edit textarea.
 * Rewrites legacy (ref: label||id) tokens to the current (mac: label||id) format
 * so that editors always see the consistent Turkish keyword regardless of when
 * the entry was originally written.
 */
function normalizeContentForEdit(content: string): string {
  return content.replace(/\(ref:\s*(.*?)\|\|(\d+)\)/g, "(mac: $1||$2)");
}

function collectSpans(content: string): ContentSpan[] {
  // Every image of this entry, in document order. Passed to each EntryImage so
  // the lightbox can navigate between the images of the same entry.
  const galleryUrls = collectEntryImageUrls(content);
  const spans: ContentSpan[] = [];
  let m: RegExpExecArray | null;

  // --- mac: current match ref format (mac: label||matchId) ---
  MAC_REF_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = MAC_REF_PATTERN.exec(content)) !== null) {
    const label = m[1].trim();
    const matchId = m[2];
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`mac-${m.index}`}
          to={`/mac--${matchId}`}
          className="entry-ref-link"
          title={`maça git: ${label}`}
        >
          {label}
        </Link>
      ),
    });
  }

  // --- ref: new format (ref: label||matchId) — legacy, kept for backward compatibility ---
  REF_NEW_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = REF_NEW_PATTERN.exec(content)) !== null) {
    const label = m[1].trim();
    const matchId = m[2];
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`ref-${m.index}`}
          to={`/mac--${matchId}`}
          className="entry-ref-link"
          title={`maça git: ${label}`}
        >
          {label}
        </Link>
      ),
    });
  }

  // --- ref: legacy format (ref/bkz: slug--id) ---
  REF_LEGACY_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = REF_LEGACY_PATTERN.exec(content)) !== null) {
    // Skip if already covered by new-format span
    if (spans.some((s) => s.index === m!.index)) continue;
    const slug = m[2];
    const label = slug.replace(/--\d+$/, "").replace(/-/g, " ");
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`legacy-${m.index}`}
          to={`/${slug}`}
          className="entry-ref-link"
          title={`maça git: ${label}`}
        >
          {label}
        </Link>
      ),
    });
  }

  // --- bold: (b: text) ---
  BOLD_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = BOLD_PATTERN.exec(content)) !== null) {
    spans.push({
      index: m.index,
      length: m[0].length,
      node: <strong key={`b-${m.index}`}>{m[1]}</strong>,
    });
  }

  // --- italic: (i: text) ---
  ITALIC_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = ITALIC_PATTERN.exec(content)) !== null) {
    spans.push({
      index: m.index,
      length: m[0].length,
      node: <em key={`i-${m.index}`}>{m[1]}</em>,
    });
  }

  // --- link: (link: url|display text) — routed through the external link warning page ---
  LINK_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = LINK_PATTERN.exec(content)) !== null) {
    const url = m[1].trim();
    const displayText = m[2].trim();
    const warningPath = `/dis-link?url=${encodeURIComponent(url)}`;
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`link-${m.index}`}
          to={warningPath}
          className="entry-ext-link"
          title={url}
        >
          {displayText}
        </Link>
      ),
    });
  }

  // --- entry ref: (#id) ---
  ENTRY_REF_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = ENTRY_REF_PATTERN.exec(content)) !== null) {
    const entryId = m[1];
    spans.push({
      index: m.index,
      length: m[0].length,
      node: <EntryIdRefLink key={`eref-${m.index}`} entryId={entryId} />,
    });
  }

  // --- kisi: person reference (kisi: displayName||slug) — links to /kisi/{slug} ---
  PERSON_REF_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PERSON_REF_PATTERN.exec(content)) !== null) {
    const label = m[1].trim();
    const slug = m[2].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`kisi-${m.index}`}
          to={`/kisi/${slug}`}
          className="entry-person-ref-link"
          title={`kişiye git: ${label}`}
        >
          {label}
        </Link>
      ),
    });
  }

  // --- kulup: club reference (kulup: displayName||slug) — links to /kulup/{slug} ---
  CLUB_REF_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = CLUB_REF_PATTERN.exec(content)) !== null) {
    const label = m[1].trim();
    const slug = m[2].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`kulup-${m.index}`}
          to={`/kulup/${slug}`}
          className="entry-person-ref-link"
          title={`kulübe git: ${label}`}
        >
          {label}
        </Link>
      ),
    });
  }

  // --- takim: team reference (takim: label||id) — links to /takim/{tail} ---
  TEAM_REF_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = TEAM_REF_PATTERN.exec(content)) !== null) {
    const label = m[1].trim();
    const slugPath = m[2].trim(); // "{id}", or legacy "clubSlug/teamSlug"
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`takim-${m.index}`}
          to={`/takim/${slugPath}`}
          className="entry-person-ref-link"
          title={`takıma git: ${label}`}
        >
          {label}
        </Link>
      ),
    });
  }

  // --- baslik: custom topic reference (baslik: title||slug) — links to /gundem/{slug} ---
  TOPIC_REF_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = TOPIC_REF_PATTERN.exec(content)) !== null) {
    const label = m[1].trim();
    const slug = m[2].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`baslik-${m.index}`}
          to={`/gundem/${slug}`}
          className="entry-person-ref-link"
          title={`başlığa git: ${label}`}
        >
          {label}
        </Link>
      ),
    });
  }

  // --- bkz-* variants: rendered as a parenthesized "(bkz: <link>)" note ---
  // The parens and the "bkz:" prefix stay plain text; only the label is a link,
  // reusing the same link classes as the inline variants for consistent styling.
  const bkzVariants: Array<{
    pattern: RegExp;
    keyPrefix: string;
    buildTo: (tail: string) => string;
    buildTitle: (label: string) => string;
    className: string;
  }> = [
    { pattern: BKZ_MAC_REF_PATTERN, keyPrefix: "bkz-mac", buildTo: (tail) => `/mac--${tail}`, buildTitle: (label) => `maça git: ${label}`, className: "entry-ref-link" },
    { pattern: BKZ_PERSON_REF_PATTERN, keyPrefix: "bkz-kisi", buildTo: (tail) => `/kisi/${tail}`, buildTitle: (label) => `kişiye git: ${label}`, className: "entry-person-ref-link" },
    { pattern: BKZ_CLUB_REF_PATTERN, keyPrefix: "bkz-kulup", buildTo: (tail) => `/kulup/${tail}`, buildTitle: (label) => `kulübe git: ${label}`, className: "entry-person-ref-link" },
    { pattern: BKZ_TEAM_REF_PATTERN, keyPrefix: "bkz-takim", buildTo: (tail) => `/takim/${tail}`, buildTitle: (label) => `takıma git: ${label}`, className: "entry-person-ref-link" },
    { pattern: BKZ_TOPIC_REF_PATTERN, keyPrefix: "bkz-baslik", buildTo: (tail) => `/gundem/${tail}`, buildTitle: (label) => `başlığa git: ${label}`, className: "entry-person-ref-link" },
  ];
  for (const variant of bkzVariants) {
    variant.pattern.lastIndex = 0;
    // eslint-disable-next-line no-loop-func
    while ((m = variant.pattern.exec(content)) !== null) {
      const label = m[1].trim();
      const tail = m[2].trim();
      spans.push({
        index: m.index,
        length: m[0].length,
        node: (
          <span key={`${variant.keyPrefix}-${m.index}`} className="entry-bkz-ref">
            {"(bkz: "}
            <Link
              to={variant.buildTo(tail)}
              className={variant.className}
              title={variant.buildTitle(label)}
            >
              {label}
            </Link>
            {")"}
          </span>
        ),
      });
    }
  }

  // --- spoiler: collapsed block — hidden until the reader clicks the toggle ---
  // eslint-disable-next-line no-loop-func
  for (let block = findBlockToken(content, "spoiler", 0); block; block = findBlockToken(content, "spoiler", block.index + block.length)) {
    const hidden = block.content.trim();
    spans.push({
      index: block.index,
      length: block.length,
      node: <SpoilerBlock key={`spoiler-${block.index}`} text={hidden} />,
    });
  }

  // --- alinti: quote block — faded background, italic, wrapped in quotes ---
  // eslint-disable-next-line no-loop-func
  for (let block = findBlockToken(content, "alinti", 0); block; block = findBlockToken(content, "alinti", block.index + block.length)) {
    const quoted = block.content.trim();
    spans.push({
      index: block.index,
      length: block.length,
      node: (
        <blockquote key={`alinti-${block.index}`} className="entry-quote-block">
          {"\u201C"}{renderBasicMarkup(quoted, galleryUrls)}{"\u201D"}
        </blockquote>
      ),
    });
  }

  // --- gorsel: (gorsel: https://...) — renders as a block-level img ---
  GORSEL_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = GORSEL_PATTERN.exec(content)) !== null) {
    const url = m[1].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <EntryImage key={`gorsel-${m.index}`} src={url} gallery={galleryUrls} />
      ),
    });
  }

  // Sort by position
  spans.sort((a, b) => a.index - b.index);

  // Drop spans fully contained inside an earlier (outer) span — e.g. a (b: ...)
  // token pasted inside an (alinti: ...) quote must not render a second time.
  const filtered: ContentSpan[] = [];
  let coveredUntil = -1;
  for (const s of spans) {
    if (s.index < coveredUntil) continue;
    filtered.push(s);
    coveredUntil = s.index + s.length;
  }
  return filtered;
}

/**
 * Parse entry content and convert markup tags into React nodes.
 * Supports: (ref: ...), (b: ...), (i: ...), (link: url|text)
 * All other text is rendered as plain text.
 */
function renderContent(content: string): React.ReactNode[] {
  // Drop the line breaks around image tokens so two consecutive images are
  // separated by their frame margins only, not by an extra empty line.
  content = normalizeImageTokenSpacing(content);
  const spans = collectSpans(content);
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const span of spans) {
    // Plain text before this span
    if (span.index > lastIndex) {
      nodes.push(content.slice(lastIndex, span.index));
    }
    nodes.push(span.node);
    lastIndex = span.index + span.length;
  }

  // Remaining plain text after the last span
  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex));
  }

  return nodes;
}


interface EntryRowProps {
  entry: EntryModel;
  onVoteUpdate?: (entryId: number, updates: Partial<EntryModel>) => void;
  // Issue B: called after successful soft-delete so parent can remove the row
  onDelete?: (entryId: number) => void;
  // Issue C: whether this entry is currently pinned to the viewing user's profile
  isPinned?: boolean;
  // Issue C: called after pin/unpin so parent (ProfilePage) can refresh pinned state
  onPinChange?: (pinnedEntry: EntryModel | null) => void;
}

export const EntryRow: React.FC<EntryRowProps> = ({ entry, onVoteUpdate, onDelete, isPinned: isPinnedProp = false, onPinChange }) => {
  // Issue C: derive isPinned from global auth context so the ... button
  // reflects pinned state even in the main feed (not just on ProfilePage)
  const { pinnedEntryId, setPinnedEntryId, isAuthenticated, user } = useAuth();
  const isPinned = pinnedEntryId === entry.id || isPinnedProp;
  const { preferences } = usePreferences();
  // entryMaxLength drives the inline edit textarea cap and its counter, so a
  // lowered admin setting is visible before the user presses save
  const { entryPreviewLineLimit, entryMaxLength } = useSiteSettings();
  // Use a ref so handleDeleteEntry always reads the freshest preference value,
  // preventing any potential stale-closure issue across renders.
  const preferencesRef = useRef(preferences);
  useEffect(() => {
    preferencesRef.current = preferences;
  }, [preferences]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isVoting, setIsVoting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showRateLimitToast, setShowRateLimitToast] = useState(false);
  
  // Check if entry IDs should be shown (default true if not set)
  const showEntryIds = preferences?.showEntryIds !== false;

  // Ref on the content div — used by useLayoutEffect to detect CSS line-clamp overflow
  const contentRef = useRef<HTMLDivElement>(null);
  // Collapsed by default; set to true when the user clicks "devamını gör"
  const [isExpanded, setIsExpanded] = useState(false);
  // True when CSS line-clamp is actually clipping content (scrollHeight > clientHeight)
  const [isTruncated, setIsTruncated] = useState(false);

  // After each render, measure whether the clamp is hiding any content.
  // Runs as a layout effect so the check happens before the browser paints,
  // preventing a visible flash of the "show more" link on short entries.
  useLayoutEffect(() => {
    if (isExpanded || entryPreviewLineLimit === 0) {
      setIsTruncated(false);
      return;
    }
    const el = contentRef.current;
    if (!el) return;
    // scrollHeight is the full content height regardless of overflow:hidden;
    // clientHeight is the visible clamped height — difference means clipping is active.
    setIsTruncated(el.scrollHeight > el.clientHeight);
  }, [entry.content, entryPreviewLineLimit, isExpanded]);

  // Local state for optimistic updates
  const [plusCount, setPlusCount] = useState(entry.plusCount);
  const [minusCount, setMinusCount] = useState(entry.minusCount);
  const [favCount, setFavCount] = useState(entry.favCount);
  const [currentUserRating, setCurrentUserRating] = useState<VoteTypeEnum | null>(entry.currentUserRating);
  const [currentUserHasFav, setCurrentUserHasFav] = useState(entry.currentUserHasFav);

  // Voters popup state
  const [votersPopup, setVotersPopup] = useState<{
    isOpen: boolean;
    voteType: VoteTypeEnum | null;
    voters: VoterResponse[];
    isLoading: boolean;
  }>({
    isOpen: false,
    voteType: null,
    voters: [],
    isLoading: false,
  });
  const popupRef = useRef<HTMLDivElement>(null);

  // Sync local state when entry changes or user auth state changes
  // Reset when: entry.id changes (different entry) OR user's vote status changes (login/logout)
  useEffect(() => {
    setPlusCount(entry.plusCount);
    setMinusCount(entry.minusCount);
    setFavCount(entry.favCount);
    setCurrentUserRating(entry.currentUserRating);
    setCurrentUserHasFav(entry.currentUserHasFav);
  }, [entry.id, entry.plusCount, entry.minusCount, entry.favCount, entry.currentUserRating, entry.currentUserHasFav]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setVotersPopup((prev) => ({ ...prev, isOpen: false }));
      }
    };

    if (votersPopup.isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [votersPopup.isOpen]);

  // Check if user can vote
  const isOwnEntry = user?.id === entry.userId;
  const [menuOpen, setMenuOpen] = useState(false);

  // Issue B: delete confirmation state (inline in the dropdown)
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reason for admin/mod delete of another user's entry
  const [deleteReason, setDeleteReason] = useState("");

  // Report modal state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  // Track if the current user has already reported this entry (set after successful submit or API check)
  const [hasReported, setHasReported] = useState(false);
  const [showAlreadyReportedToast, setShowAlreadyReportedToast] = useState(false);
  const [isCheckingReport, setIsCheckingReport] = useState(false);

  const handleReportClick = async () => {
    setMenuOpen(false);
    // If we already know it was reported this session, show toast immediately
    if (hasReported) {
      setShowAlreadyReportedToast(true);
      setTimeout(() => setShowAlreadyReportedToast(false), TOAST_DURATION_NORMAL);
      return;
    }
    // Otherwise check with backend before opening modal
    setIsCheckingReport(true);
    try {
      const { reported } = await getReportStatus(entry.id);
      if (reported) {
        setHasReported(true);
        setShowAlreadyReportedToast(true);
        setTimeout(() => setShowAlreadyReportedToast(false), TOAST_DURATION_NORMAL);
      } else {
        setReportModalOpen(true);
      }
    } catch {
      // On error just open the modal — the submit will handle any conflict
      setReportModalOpen(true);
    } finally {
      setIsCheckingReport(false);
    }
  };

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(normalizeContentForEdit(entry.content));
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editShowPreview, setEditShowPreview] = useState(false);
  const editTextareaRef = useRef<ChipEditorHandle>(null);
  // Use role for voting restrictions — status could be FROZEN/DELETED while role stays ROOKIE
  const isRookie = user?.role === "ROOKIE";
  const menuRef = useRef<HTMLDivElement>(null);

  // Reason for admin/mod edit of another user's entry
  const [editReason, setEditReason] = useState("");

  // Sync editContent when the entry prop changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditContent(normalizeContentForEdit(entry.content));
    }
  }, [entry.content, isEditing]);

  // Focus the edit box when edit mode opens
  useEffect(() => {
    if (isEditing && editTextareaRef.current) {
      const ta = editTextareaRef.current;
      ta.focus();
      // Move cursor to end. The length comes from editContent rather than from
      // the editor: ChipEditor exposes only the caret members a textarea would,
      // and its own text is this same state anyway.
      ta.selectionStart = ta.selectionEnd = editContent.length;
    }
  // editContent is intentionally not a dependency — this runs when edit mode
  // opens, not on every keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // Close the 3-dot menu when user clicks outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setDeleteConfirm(false); // Reset confirmation when closing menu
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const canVote = isAuthenticated && !isOwnEntry && !isRookie;
  
  // Check if user can see vote counts (admin or moderator only)
  const canSeeVoteCounts = user?.role === "ADMIN" || user?.role === "MODERATOR";

  // Admins and moderators can delete any entry, not just their own
  const isAdminOrMod = user?.role === "ADMIN" || user?.role === "MODERATOR";

  // Handle saving edited entry content.
  // If admin/mod is editing another user's entry, use the admin endpoint
  // so that editedBy and editReason are recorded for the audit trail.
  const handleSaveEdit = async () => {
    const trimmed = editContent.trim();
    if (!trimmed) return;
    // Stored content is always lowercase, so compare the normalized form —
    // otherwise re-typing the same text with different casing would trigger
    // a pointless save that bumps editedAt.
    if (lowercaseEntryContent(trimmed) === entry.content) {
      // No change — just exit edit mode
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setEditError("");
    try {
      if (isAdminOrMod && !isOwnEntry) {
        // Admin/mod editing another user's entry — use admin endpoint with reason
        const updated = await updateAdminEntry(entry.id, {
          content: trimmed,
          reason: editReason.trim() || undefined,
        });
        entry.content = updated.content;
        entry.editedAt = parseServerDate(updated.editedAt);
      } else {
        // Owner editing their own entry — use regular endpoint
        const updated = await updateEntry(entry.id, trimmed);
        // Update the local entry content via prop mutation isn't ideal —
        // parent component should refresh, but we optimistically update here
        entry.content = updated.content;
        entry.editedAt = updated.editedAt;
      }
      setEditContent(normalizeContentForEdit(entry.content));
      setIsEditing(false);
      setEditReason("");
    } catch (err: any) {
      // Parse structured backend error codes (ENTRY_EDIT_WINDOW_EXPIRED, ENTRY_TOO_SHORT, etc.)
      // and display a translated message instead of the generic fallback.
      const msg = err instanceof Error ? err.message : "";
      setEditError(parseApiError(msg, t) || t("entry_edit_error"));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * "Send message" entry menu action.
   *
   * Navigates to the messages page carrying the entry author in router state —
   * the same contract the profile message button and the online users modal use,
   * so MessagesPage opens (or creates) the conversation with that user instead of
   * landing on the empty "select a conversation" panel.
   *
   * In addition it passes a draft that references this entry, so the recipient
   * immediately knows which entry the message is about.
   */
  const handleSendMessageToAuthor = () => {
    setMenuOpen(false);
    navigate("/mesajlar", {
      state: {
        recipientId:       entry.userId,
        recipientUsername: entry.username,
        recipientRole:     entry.authorRole,
        draftText:         t("messages_entry_reference_draft", { entryId: entry.id }),
      },
    });
  };

  // Issue A: Handle copy entry ID to clipboard
  // Bug fix: navigator.clipboard is only available in secure contexts (HTTPS).
  // Added execCommand fallback for HTTP environments and older browsers.
  // The event is optional: the hover badge passes it, the share menu item does not
  const handleCopyEntryId = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const text = `(#${entry.id})`;

    // Primary: Clipboard API (requires HTTPS / secure context)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), TOAST_DURATION_SHORT);
        return;
      } catch {
        // Fall through to execCommand fallback below
      }
    }

    // Fallback: execCommand (works on HTTP and older browsers)
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "0";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand("copy");
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), TOAST_DURATION_SHORT);
    } catch (err) {
      console.error("Failed to copy entry ID:", err);
    } finally {
      document.body.removeChild(textarea);
    }
  };

  // Issue B: Handle soft-delete of own entry.
  // If the permanentDelete preference is enabled, also hard-delete from trash
  // immediately after soft-delete so the entry bypasses the trash bin entirely.
  // Always read preferencesRef.current so the latest value is used, not a stale closure.
  // When admin/mod deletes another user's entry, the admin endpoint is used so that
  // deletedBy and deleteReason are correctly recorded in the audit trail.
  const handleDeleteEntry = async () => {
    setIsDeleting(true);
    let success = false;
    try {
      if (isAdminOrMod && !isOwnEntry) {
        // Admin/mod deleting another user's entry — use admin endpoint with reason
        await deleteAdminEntry(entry.id, {
          reason: deleteReason.trim() || undefined,
        });
      } else {
        // Owner deleting their own entry — use regular endpoint
        await deleteEntry(entry.id);
        if (preferencesRef.current?.permanentDelete) {
          // Permanently remove without going to trash
          await hardDeleteFromTrash(entry.id);
        }
      }
      setMenuOpen(false);
      setDeleteConfirm(false);
      setDeleteReason("");
      success = true;
    } catch (err: any) {
      console.error("Delete failed:", err);
      setIsDeleting(false);
      setDeleteConfirm(false);
    } finally {
      // Notify parent to remove this entry from the list only on full success
      if (success) {
        onDelete?.(entry.id);
      }
    }
  };

  // Issue C: Handle pin / unpin
  const [isPinning, setIsPinning] = useState(false);

  const handlePinEntry = async () => {
    if (isPinning) return;
    setIsPinning(true);
    try {
      if (isPinned) {
        // Unpin: clear the current pin
        await unpinEntry();
        // Issue C: update global context so all EntryRows reflect the change immediately
        setPinnedEntryId(null);
        onPinChange?.(null);
      } else {
        // Pin: set this entry as pinned; backend returns updated profile
        const updatedProfile = await pinEntry(entry.id);
        // Issue C: update global context
        setPinnedEntryId(entry.id);
        onPinChange?.(updatedProfile.pinnedEntry);
      }
      setMenuOpen(false);
    } catch (err) {
      console.error("Pin/unpin failed:", err);
    } finally {
      setIsPinning(false);
    }
  };

  // ── Sitewide topic pin (ADMIN + MODERATOR) ──────────────────────────────
  // Which topic this entry belongs to — exactly one of the 5 FKs is set
  const pinTarget: { type: PinTargetType; targetId: number } | null =
    entry.customTopicId != null ? { type: "CUSTOM_TOPIC", targetId: entry.customTopicId } :
    entry.matchId       != null ? { type: "MATCH",        targetId: entry.matchId } :
    entry.clubId        != null ? { type: "CLUB",         targetId: entry.clubId } :
    entry.personId      != null ? { type: "PERSON",       targetId: entry.personId } :
    entry.teamId        != null ? { type: "TEAM",         targetId: entry.teamId } :
    null;
  const { getPinnedEntryId } = usePins();
  const isTopicPinnedEntry = pinTarget != null && getPinnedEntryId(pinTarget.type, pinTarget.targetId) === entry.id;
  const [isTopicPinning, setIsTopicPinning] = useState(false);

  const handleTopicPin = async () => {
    if (!pinTarget || isTopicPinning) return;
    setIsTopicPinning(true);
    try {
      if (isTopicPinnedEntry) {
        await adminPinService.unpinEntry(pinTarget.type, pinTarget.targetId);
      } else {
        await adminPinService.pinEntry(pinTarget.type, pinTarget.targetId, entry.id);
      }
      setMenuOpen(false);
    } catch (err) {
      console.error("Topic pin/unpin failed:", err);
    } finally {
      setIsTopicPinning(false);
    }
  };

  // Handle vote click
  const handleVote = async (voteType: VoteTypeEnum) => {
    if (!canVote || isVoting) return;

    setIsVoting(true);

    // Calculate new values for optimistic update
    let newPlusCount = plusCount;
    let newMinusCount = minusCount;
    let newFavCount = favCount;
    let newCurrentUserRating = currentUserRating;
    let newCurrentUserHasFav = currentUserHasFav;

    if (voteType === VoteTypeEnum.FAV) {
      // Toggle FAV
      if (currentUserHasFav) {
        newFavCount = favCount - 1;
        newCurrentUserHasFav = false;
      } else {
        newFavCount = favCount + 1;
        newCurrentUserHasFav = true;
      }
    } else {
      // Handle UP/DOWN (mutually exclusive)
      if (currentUserRating === voteType) {
        // Toggle off - remove current rating
        if (voteType === VoteTypeEnum.UP) {
          newPlusCount = plusCount - 1;
        } else {
          newMinusCount = minusCount - 1;
        }
        newCurrentUserRating = null;
      } else {
        // Switch or add rating
        if (currentUserRating === VoteTypeEnum.UP) {
          newPlusCount = plusCount - 1;
          newMinusCount = minusCount + 1;
        } else if (currentUserRating === VoteTypeEnum.DOWN) {
          newMinusCount = minusCount - 1;
          newPlusCount = plusCount + 1;
        } else {
          // No previous rating
          if (voteType === VoteTypeEnum.UP) {
            newPlusCount = plusCount + 1;
          } else {
            newMinusCount = minusCount + 1;
          }
        }
        newCurrentUserRating = voteType;
      }
    }

    // Apply optimistic update to local state
    setPlusCount(newPlusCount);
    setMinusCount(newMinusCount);
    setFavCount(newFavCount);
    setCurrentUserRating(newCurrentUserRating);
    setCurrentUserHasFav(newCurrentUserHasFav);

    try {
      await castVote(entry.id, voteType);
      // Notify parent component with NEW values
      if (onVoteUpdate) {
        onVoteUpdate(entry.id, {
          plusCount: newPlusCount,
          minusCount: newMinusCount,
          favCount: newFavCount,
          currentUserRating: newCurrentUserRating,
          currentUserHasFav: newCurrentUserHasFav,
        });
      }
    } catch (error) {
      // Revert optimistic update on error
      setPlusCount(entry.plusCount);
      setMinusCount(entry.minusCount);
      setFavCount(entry.favCount);
      setCurrentUserRating(entry.currentUserRating);
      setCurrentUserHasFav(entry.currentUserHasFav);

      // Show rate limit toast if it's a rate limit error
      if (error instanceof RateLimitError) {
        setShowRateLimitToast(true);
        setTimeout(() => setShowRateLimitToast(false), TOAST_DURATION_NORMAL);
      }

      console.error("Vote failed:", error);
    } finally {
      setIsVoting(false);
    }
  };

  // Handle vote count click to show voters popup
  const handleVoteCountClick = async (
    e: React.MouseEvent,
    voteType: VoteTypeEnum,
    count: number
  ) => {
    e.stopPropagation(); // Prevent button click

    if (count === 0) return;

    // If clicking on the same type that's already open, close it
    if (votersPopup.isOpen && votersPopup.voteType === voteType) {
      setVotersPopup((prev) => ({ ...prev, isOpen: false }));
      return;
    }

    setVotersPopup({
      isOpen: true,
      voteType,
      voters: [],
      isLoading: true,
    });

    try {
      const voters = await getVoters(entry.id, voteType);
      setVotersPopup({
        isOpen: true,
        voteType,
        voters,
        isLoading: false,
      });
    } catch (error) {
      console.error("Failed to fetch voters:", error);
      setVotersPopup((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // Get popup title based on vote type
  const getPopupTitle = (voteType: VoteTypeEnum | null): string => {
    if (!voteType) return "";
    switch (voteType) {
      case VoteTypeEnum.UP:
        return t("voters_plus_title");
      case VoteTypeEnum.DOWN:
        return t("voters_minus_title");
      case VoteTypeEnum.FAV:
        return t("voters_fav_title");
      default:
        return "";
    }
  };

  // Handle button press visual feedback
  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.classList.add("pressing");
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.classList.remove("pressing");
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.classList.remove("pressing");
  };

  // Check if entry author is a ROOKIE - used for grey styling
  const isRookieAuthor = entry.authorRole === "ROOKIE";

  // Share message: "topic title (@author)" — exactly one of the five topic
  // fields is set on any entry, so the first non-empty one wins.
  const entryTopicTitle =
    entry.customTopicTitle ||
    entry.personFullName ||
    entry.clubName ||
    entry.teamName ||
    (entry.homeTeam && entry.awayTeam ? `${entry.homeTeam} - ${entry.awayTeam}` : "");
  const entryShareTitle = entryTopicTitle
    ? `${entryTopicTitle} (@${entry.username})`
    : `@${entry.username}`;

  // A rookie row is dimmed on purpose, but `opacity` on the row creates a stacking
  // group its children cannot escape: the 3-dot dropdown and the inline edit box
  // inherited the dimming and came out barely readable. entry-row--active lifts the
  // dimming for as long as the row is being acted on, the same way
  // entry-row--popup-open already does for the voters popup.
  return (
    <div 
      id={`entry-${entry.id}`}
      className={`entry-row ${isRookieAuthor ? "entry-row--rookie" : ""} ${votersPopup.isOpen ? "entry-row--popup-open" : ""} ${menuOpen || isEditing || deleteConfirm ? "entry-row--active" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Entry ID Badge - shows on hover if showEntryIds preference is enabled */}
      {showEntryIds && (
        <div 
          className={`entry-id-badge ${isHovered ? "visible" : ""}`}
          onClick={handleCopyEntryId}
          title={t("click_to_copy_entry_id")}
        >
          #{entry.id}
        </div>
      )}

      {/* Content — normal view or inline edit mode */}
      {isEditing ? (
        <div className="entry-edit-area">
          {/* Inline preview — shown above textarea when toggled */}
          {/* Preview shows the lowercased form — exactly what will be stored after saving */}
          {editShowPreview && editContent.trim() && (
            <div className="entry-edit-preview">
              <div className="entry-edit-preview__label">{t("entry_edit_preview")}</div>
              <div className="entry-edit-preview__body">{renderContent(lowercaseEntryContent(editContent))}</div>
            </div>
          )}
          {/* Toolbar row — same buttons as the new entry form */}
          <EntryEditorToolbar
            textareaRef={editTextareaRef}
            content={editContent}
            setContent={setEditContent}
          />
          <ChipEditor
            ref={editTextareaRef}
            className="entry-edit-textarea"
            value={editContent}
            onChange={setEditContent}
            maxLength={entryMaxLength}
            placeholder={t("entry_edit_placeholder")}
            disabled={isSaving}
          />
          {editError && <div className="entry-edit-error">{editError}</div>}

          {/* Reason field — only shown when admin/mod is editing another user's entry */}
          {isAdminOrMod && !isOwnEntry && (
            <div className="entry-edit-reason-row">
              <input
                className="entry-edit-reason-input"
                type="text"
                placeholder={t("entry_edit_reason_hint")}
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                maxLength={500}
                disabled={isSaving}
              />
            </div>
          )}

          <div className="entry-edit-actions">
            <span className="entry-edit-charcount">{editContent.length} / {entryMaxLength}</span>
            {/* Clear button — only visible when there is content to clear */}
            {editContent.length > 0 && (
              <button
                className="epf-clear-btn"
                title={t("admin_clear_filters", "Clear")}
                onClick={() => setEditContent("")}
                disabled={isSaving}
                type="button"
              >
                <RiDeleteBin6Line size={16} />
              </button>
            )}
            <button
              className="entry-edit-btn entry-edit-btn--preview"
              onClick={() => setEditShowPreview((p) => !p)}
              disabled={!editContent.trim()}
              type="button"
            >
              {t("entry_edit_preview")}
            </button>
            <button
              className="entry-edit-btn entry-edit-btn--cancel"
              onClick={() => { setIsEditing(false); setEditContent(normalizeContentForEdit(entry.content)); setEditError(""); setEditShowPreview(false); setEditReason(""); }}
              disabled={isSaving}
            >
              {t("entry_edit_cancel")}
            </button>
            <button
              className="entry-edit-btn entry-edit-btn--save"
              onClick={handleSaveEdit}
              disabled={isSaving || !editContent.trim()}
            >
              {isSaving ? t("entry_edit_saving") : t("entry_edit_save")}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* CSS line-clamp truncation: clamp applied via class + inline style.
              The "show more" link lives OUTSIDE the clamped div so it is never
              hidden by overflow:hidden. isTruncated is measured by useLayoutEffect. */}
          <div
            ref={contentRef}
            className={`content${!isExpanded && entryPreviewLineLimit > 0 ? " content--clamped" : ""}`}
            style={!isExpanded && entryPreviewLineLimit > 0
              ? { WebkitLineClamp: entryPreviewLineLimit }
              : undefined}
          >
            {renderContent(entry.content)}
          </div>
          {isTruncated && !isExpanded && (
            <span
              className="entry-show-more"
              onClick={() => setIsExpanded(true)}
            >
              {t("entry_show_more")}
            </span>
          )}
        </>
      )}

      {/* Bottom row with buttons and metadata */}
      <div className="bottom-row">
        {/* Vote buttons (left-aligned) - Only show when authenticated */}
        {isAuthenticated && (
          <div className="button-group" ref={popupRef}>
            {/* Plus button */}
            <div className="vote-btn-wrapper">
              <button
                className={`btn btn-plus ${currentUserRating === VoteTypeEnum.UP ? "active" : ""} ${!canVote ? "disabled" : ""}`}
                onClick={() => handleVote(VoteTypeEnum.UP)}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                disabled={!canVote || isVoting}
                title={isOwnEntry ? t("vote_error_own_entry") : isRookie ? t("vote_error_rookie") : t("vote_plus")}
              >
                <span className="vote-symbol">+</span>
              </button>
              {canSeeVoteCounts && plusCount > 0 && (
                <button
                  className={`btn btn-count btn-plus-count ${currentUserRating === VoteTypeEnum.UP ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVoteCountClick(e, VoteTypeEnum.UP, plusCount);
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                >
                  <span className="vote-count-text">{plusCount}</span>
                </button>
              )}
            </div>

            {/* Minus button */}
            <div className="vote-btn-wrapper">
              <button
                className={`btn btn-minus ${currentUserRating === VoteTypeEnum.DOWN ? "active" : ""} ${!canVote ? "disabled" : ""}`}
                onClick={() => handleVote(VoteTypeEnum.DOWN)}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                disabled={!canVote || isVoting}
                title={isOwnEntry ? t("vote_error_own_entry") : isRookie ? t("vote_error_rookie") : t("vote_minus")}
              >
                <span className="vote-symbol">-</span>
              </button>
              {canSeeVoteCounts && minusCount > 0 && (
                <button
                  className={`btn btn-count btn-minus-count ${currentUserRating === VoteTypeEnum.DOWN ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVoteCountClick(e, VoteTypeEnum.DOWN, minusCount);
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                >
                  <span className="vote-count-text">{minusCount}</span>
                </button>
              )}
            </div>

            {/* Favorite button (sport-specific icon) */}
            <div className="vote-btn-wrapper">
              <button
                className={`btn btn-favorite ${currentUserHasFav ? "active" : ""} ${!canVote ? "disabled" : ""}`}
                onClick={() => handleVote(VoteTypeEnum.FAV)}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                disabled={!canVote || isVoting}
                title={isOwnEntry ? t("vote_error_own_entry") : isRookie ? t("vote_error_rookie") : t("vote_favorite")}
              >
                <span className="vote-symbol">{getSportIcon(entry.sportType, 14)}</span>
              </button>
              {favCount > 0 && (
                <button
                  className={`btn btn-count btn-fav-count ${currentUserHasFav ? "active" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVoteCountClick(e, VoteTypeEnum.FAV, favCount);
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                >
                  <span className="vote-count-text">{favCount}</span>
                </button>
              )}
            </div>

            {/* Voters popup */}
            {votersPopup.isOpen && (
              <div className="voters-popup">
                <div className="voters-popup-header">
                  {getPopupTitle(votersPopup.voteType)}
                </div>
                <div className="voters-popup-content">
                  {votersPopup.isLoading ? (
                    <div className="voters-loading">{t("loading")}</div>
                  ) : votersPopup.voters.length === 0 ? (
                    <div className="voters-empty">{t("no_voters")}</div>
                  ) : (
                    <ul className="voters-list">
                      {votersPopup.voters.map((voter) => (
                        <li key={voter.userId} className="voter-item">
                          <Link 
                            to={generateProfileUrl(voter.username, voter.userId)}
                            className="voter-link"
                          >
                            @{voter.username}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Metadata - Author, Date, and 3-dot menu */}
        <div className="metadata-wrapper">
          <Link 
            to={generateProfileUrl(entry.username, entry.userId)} 
            className="metadata-author"
            title={`@${entry.username}`}
          >
            {/* Full username is always visible (no ellipsis); on narrow rows it
                wraps on spaces and the date box moves to the next line */}
            <span className="user-link">@{entry.username}</span>
          </Link>
          <div className="metadata-date">
            <span className="entry-row__date-time">
              {formatDateTime(entry.createdAt)}
            </span>
          </div>

          {/* Share this entry — available to guests as well, so the 3-dot menu's
              authenticated-only gate does not apply here */}
          <ShareMenu
            url={buildEntryShareUrl(entry)}
            title={entryShareTitle}
            variant="compact"
            className="entry-row__share"
            extraItems={[
              {
                key: "copy-entry-id",
                label: t("share_menu_copy_entry_id", "entry no kopyala"),
                icon: <RiHashtag />,
                onClick: () => { void handleCopyEntryId(); },
              },
            ]}
          />

          {/* 3-dot context menu — only shown when authenticated */}
          {isAuthenticated && (
            <div className="entry-menu" ref={menuRef}>
              <button
                className={`entry-menu__trigger ${isPinned ? "entry-menu__trigger--pinned" : ""}`}
                title={t("entry_options", "Options")}
                onClick={(e) => { e.stopPropagation(); setMenuOpen((p) => !p); setDeleteConfirm(false); }}
              >
                <RiMoreLine />
              </button>

              {menuOpen && (
                <div className="entry-menu__dropdown">
                  {isOwnEntry ? (
                    <>
                      {/* Owner actions */}
                      <button className="entry-menu__item" onClick={() => { setIsEditing(true); setMenuOpen(false); }}>
                        <RiEditLine className="entry-menu__icon" />
                        {t("entry_menu_edit")}
                      </button>

                      <button
                        className="entry-menu__item entry-menu__item--danger"
                        onClick={() => { setDeleteConfirm(true); setMenuOpen(false); }}
                      >
                        <RiDeleteBinLine className="entry-menu__icon" />
                        {t("entry_menu_delete")}
                      </button>

                      <button
                        className={`entry-menu__item ${isPinned ? "entry-menu__item--active" : ""}`}
                        onClick={handlePinEntry}
                        disabled={isPinning}
                      >
                        <RiPushpinLine className="entry-menu__icon" />
                        {isPinning ? "..." : isPinned ? t("entry_menu_unpin_from_profile") : t("entry_menu_pin_to_profile")}
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Other user actions */}
                      <button className="entry-menu__item" onClick={handleSendMessageToAuthor}>
                        <RiMailLine className="entry-menu__icon" />
                        {t("entry_menu_send_message")}
                      </button>

                      {/* Report button — checks backend before opening modal */}
                      <button
                        className={`entry-menu__item entry-menu__item--danger ${hasReported ? "entry-menu__item--reported" : ""}`}
                        onClick={handleReportClick}
                        disabled={isCheckingReport}
                      >
                        <RiAlarmWarningLine className="entry-menu__icon" />
                        {isCheckingReport ? "..." : hasReported ? t("report_already_reported_label") : t("entry_menu_report")}
                      </button>

                      {/* Admin/mod can edit any entry directly from the entry row */}
                      {isAdminOrMod && (
                        <button
                          className="entry-menu__item"
                          onClick={() => { setIsEditing(true); setEditReason(""); setMenuOpen(false); }}
                        >
                          <RiEditLine className="entry-menu__icon" />
                          {t("entry_menu_edit")}
                        </button>
                      )}

                      {/* Admin/mod can soft-delete any entry directly from the entry row */}
                      {isAdminOrMod && (
                        <button
                          className="entry-menu__item entry-menu__item--danger"
                          onClick={() => { setDeleteReason(""); setDeleteConfirm(true); setMenuOpen(false); }}
                        >
                          <RiDeleteBinLine className="entry-menu__icon" />
                          {t("entry_menu_delete")}
                        </button>
                      )}
                    </>
                  )}

                  {/* Admin/mod: pin this entry to the top of its topic page (sitewide) */}
                  {isAdminOrMod && pinTarget && (
                    <button
                      className={`entry-menu__item ${isTopicPinnedEntry ? "entry-menu__item--active" : ""}`}
                      onClick={handleTopicPin}
                      disabled={isTopicPinning}
                    >
                      <RiPushpinLine className="entry-menu__icon" />
                      {isTopicPinning ? "..." : isTopicPinnedEntry
                        ? t("entry_menu_unpin_from_topic", "başlıktan sabitlemeyi kaldır")
                        : t("entry_menu_pin_to_topic", "başlığa sabitle")}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Copy Toast */}
      {showCopyToast && (
        <div className="entry-copy-toast">
          {t("entry_id_copied")}
        </div>
      )}

      {/* Rate Limit Toast */}
      {showRateLimitToast && (
        <div className="entry-rate-limit-toast">
          {t("vote_rate_limit_exceeded")}
        </div>
      )}

      {/* Delete confirmation modal.
          For admin/mod deleting another user's entry, a reason input is shown
          inside the modal so the audit trail can be filled inline. */}
      {deleteConfirm && (
        <ConfirmModal
          message={t("entry_delete_confirm")}
          confirmLabel={isDeleting ? "..." : t("entry_menu_delete")}
          cancelLabel={t("entry_edit_cancel")}
          onConfirm={handleDeleteEntry}
          onCancel={() => { setDeleteConfirm(false); setDeleteReason(""); }}
          disabled={isDeleting}
        >
          {isAdminOrMod && !isOwnEntry && (
            <>
              <label className="confirm-modal__reason-label">
                {t("entry_delete_reason_label")}
              </label>
              <input
                className="confirm-modal__reason-input"
                type="text"
                placeholder={t("entry_delete_reason_hint")}
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                maxLength={500}
              />
            </>
          )}
        </ConfirmModal>
      )}

      {/* Already Reported Toast */}
      {showAlreadyReportedToast && (
        <div className="entry-copy-toast">
          {t("report_already_reported_tooltip")}
        </div>
      )}

      {/* Report modal — rendered into portal, only shown for other users' entries */}
      {reportModalOpen && (
        <ReportModal
          entryId={entry.id}
          onClose={() => setReportModalOpen(false)}
          onSuccess={() => setHasReported(true)}
        />
      )}
    </div>
  );
};